import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, X } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";

const EMPTY = { title: "", type: "正社員", desc: "", detail: "", tags: "", order: 0, published: true };

const inputCls = "w-full border border-line bg-ink px-3 py-2.5 text-sm text-white outline-none transition-colors focus:border-accent";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] tracking-[0.3em] text-gray-500">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

export default function AdminRecruit() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([]);
  const [savingRows, setSavingRows] = useState(false);

  const load = () =>
    api
      .get("/admin/recruit/positions")
      .then((r) => setItems(r.data))
      .catch(() => toast.error("取得に失敗しました"));

  const loadRows = () =>
    api
      .get("/admin/recruit/requirements")
      .then((r) => setRows(r.data.rows || []))
      .catch(() => toast.error("募集要項の取得に失敗しました"));

  useEffect(() => {
    load();
    loadRows();
  }, []);

  const openNew = () => {
    setForm({ ...EMPTY, order: items.length });
    setEditing({});
  };

  const openEdit = (p) => {
    setForm({ title: p.title, type: p.type, desc: p.desc || "", detail: p.detail || "", tags: (p.tags || []).join(", "), order: p.order ?? 0, published: p.published });
    setEditing(p);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, order: Number(form.order) || 0, tags: form.tags.split(",").map((x) => x.trim()).filter(Boolean) };
    try {
      if (editing.id) await api.put(`/admin/recruit/positions/${editing.id}`, payload);
      else await api.post("/admin/recruit/positions", payload);
      toast.success("保存しました");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`「${p.title}」を削除しますか？`)) return;
    try {
      await api.delete(`/admin/recruit/positions/${p.id}`);
      toast.success("削除しました");
      load();
    } catch {
      toast.error("削除に失敗しました");
    }
  };

  const setRow = (i, key) => (e) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, [key]: e.target.value } : r)));

  const saveRows = async () => {
    const clean = rows.filter((r) => r.label.trim() && r.value.trim());
    if (clean.length !== rows.length) {
      toast.error("項目名と内容の両方を入力してください（空の行は削除されます）");
    }
    setSavingRows(true);
    try {
      await api.put("/admin/recruit/requirements", { rows: clean });
      setRows(clean);
      toast.success("募集要項を保存しました");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSavingRows(false);
    }
  };

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  return (
    <div data-testid="admin-recruit-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="font-mincho text-2xl font-bold text-white">採用情報の管理</h1>
          <p className="mt-1 font-mono text-[10px] tracking-[0.3em] text-gray-500">RECRUIT PAGE MANAGEMENT</p>
        </div>
        <button
          data-testid="position-add-button"
          onClick={openNew}
          className="flex items-center gap-2 bg-accent px-5 py-2.5 font-display text-xs font-bold tracking-[0.15em] text-ink transition-colors hover:bg-accent-hover"
        >
          <Plus className="h-4 w-4" />
          職種を追加
        </button>
      </div>

      <div className="mt-8 overflow-x-auto border border-line">
        <table className="w-full min-w-[700px] text-left text-sm">
          <thead>
            <tr className="border-b border-line bg-coal font-mono text-[10px] tracking-[0.2em] text-gray-500">
              <th className="px-4 py-3">順</th>
              <th className="px-4 py-3">職種名</th>
              <th className="px-4 py-3">雇用形態</th>
              <th className="px-4 py-3">公開</th>
              <th className="px-4 py-3">操作</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} data-testid={`position-row-${p.id}`} className="border-b border-line bg-panel last:border-b-0">
                <td className="px-4 py-3 font-mono text-xs text-gray-400">{p.order}</td>
                <td className="px-4 py-3 text-xs font-bold text-white">{p.title}</td>
                <td className="whitespace-nowrap px-4 py-3 text-xs text-gray-400">{p.type}</td>
                <td className="px-4 py-3 text-xs">{p.published ? <span className="text-green-400">公開</span> : <span className="text-gray-500">非公開</span>}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <button data-testid={`position-edit-${p.id}`} onClick={() => openEdit(p)} className="border border-line p-2 text-gray-400 transition-colors hover:border-accent hover:text-accent">
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button data-testid={`position-delete-${p.id}`} onClick={() => remove(p)} className="border border-line p-2 text-gray-400 transition-colors hover:border-red-500 hover:text-red-400">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-12 border border-line bg-panel p-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="font-mincho text-lg font-bold text-white">募集要項</h2>
            <p className="mt-1 font-mono text-[10px] tracking-[0.3em] text-gray-500">REQUIREMENTS</p>
          </div>
          <button
            data-testid="requirement-add-row"
            onClick={() => setRows((rs) => [...rs, { label: "", value: "" }])}
            className="flex items-center gap-2 border border-line px-4 py-2 text-sm text-gray-400 transition-colors hover:border-accent hover:text-accent"
          >
            <Plus className="h-4 w-4" />
            行を追加
          </button>
        </div>
        <div className="mt-6 space-y-3">
          {rows.map((r, i) => (
            <div key={i} data-testid={`requirement-row-${i}`} className="flex items-start gap-3">
              <input
                data-testid={`requirement-label-${i}`}
                value={r.label}
                onChange={setRow(i, "label")}
                className={`w-40 shrink-0 ${inputCls}`}
                placeholder="給与"
              />
              <input
                data-testid={`requirement-value-${i}`}
                value={r.value}
                onChange={setRow(i, "value")}
                className={inputCls}
                placeholder="月給 220,000円 〜 350,000円"
              />
              <button
                type="button"
                data-testid={`requirement-remove-${i}`}
                onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))}
                className="shrink-0 border border-line p-2.5 text-gray-400 transition-colors hover:border-red-500 hover:text-red-400"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
        <button
          data-testid="requirements-save-button"
          onClick={saveRows}
          disabled={savingRows}
          className="mt-6 bg-accent px-8 py-3 font-display text-sm font-bold tracking-[0.2em] text-ink transition-colors hover:bg-accent-hover disabled:opacity-50"
        >
          {savingRows ? "保存中..." : "募集要項を保存する"}
        </button>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/80 p-4 backdrop-blur-sm">
          <form data-testid="position-form" onSubmit={save} className="max-h-[90vh] w-full max-w-xl space-y-4 overflow-y-auto border border-line bg-coal p-8">
            <div className="flex items-center justify-between">
              <h2 className="font-mincho text-lg font-bold text-white">{editing.id ? "職種を編集" : "職種を追加"}</h2>
              <button type="button" data-testid="position-form-close" onClick={() => setEditing(null)} className="text-gray-500 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>
            <Field label="職種名 *">
              <input data-testid="position-form-title" required value={form.title} onChange={set("title")} className={inputCls} placeholder="現場作業員（土木・造成）" />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="雇用形態">
                <input data-testid="position-form-type" value={form.type} onChange={set("type")} className={inputCls} placeholder="正社員" />
              </Field>
              <Field label="表示順（小さいほど先頭）">
                <input data-testid="position-form-order" type="number" value={form.order} onChange={set("order")} className={inputCls} />
              </Field>
            </div>
            <Field label="短い説明（カードに表示）">
              <textarea data-testid="position-form-desc" rows={2} value={form.desc} onChange={set("desc")} className={`resize-y ${inputCls}`} />
            </Field>
            <Field label="詳細（クリック時のポップアップに表示）">
              <textarea data-testid="position-form-detail" rows={4} value={form.detail} onChange={set("detail")} className={`resize-y ${inputCls}`} />
            </Field>
            <Field label="タグ（カンマ区切り）">
              <input data-testid="position-form-tags" value={form.tags} onChange={set("tags")} className={inputCls} placeholder="未経験OK, 資格取得支援" />
            </Field>
            <label className="flex items-center gap-3 text-sm text-gray-300">
              <input data-testid="position-form-published" type="checkbox" checked={form.published} onChange={set("published")} className="h-4 w-4 accent-accent" />
              公開する
            </label>
            <button data-testid="position-form-submit" type="submit" disabled={saving} className="w-full bg-accent py-3 font-display text-sm font-bold tracking-[0.2em] text-ink transition-colors hover:bg-accent-hover disabled:opacity-50">
              {saving ? "保存中..." : "保存する"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
